import React, { useRef } from 'react';
import { useHistory } from 'react-router';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { createStyles, makeStyles, TextField, Theme } from '@material-ui/core';
import { Formik, Field, FieldProps } from 'formik';
import { date, object, string } from 'yup';

import { AlarmDetails } from '../../../../models';
import { Lifecycle, SimpleRequestStatus } from '../../../../state/request';
import { PathBuilder } from 'src/routing';

import MainModal from 'src/components/ui/MainModal';
import MainInfo from './MainInfo';
import LoadWithSpinner from 'src/components/ui/Loading/LoadWithSpinner';

import { DATE_FORMAT } from 'src/data/date';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    subheading: {
      paddingTop: theme.spacing(),
    },
    noteField: {
      '& .MuiOutlinedInput-inputMultiline': {
        padding: theme.spacing(1.5),
      },
    },
    title: {
      fontSize: '1rem',
      fontWeight: 500,
      marginBottom: theme.spacing(2),
    },
    label: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      marginBottom: theme.spacing(0.5),
      textTransform: 'uppercase',
    },
    value: {
      fontSize: 16,
    },
  })
);

export const getResolveValidationSchema = (alarmDetails: AlarmDetails) => {
  return object({
    resolvedAt: date()
      .required()
      .max(new Date(Date.now() + 1000 * 60 * 5), 'Resolved date / time must be in the past.'),
    note: alarmDetails.alarmTag.acknowledgementNoteRequired
      ? string().required('Note is required to resolve.')
      : string().optional(),
  });
};

export interface AlarmResolveModalProps {
  requestStatus?: SimpleRequestStatus;
  siteId: number;
  status: string;
  triggeredDate: string;
  alarmDetails: AlarmDetails;
  handleSubmit: (
    siteId: number,
    alarmTagId: string,
    alarmId: string,
    resolvedAt: Date,
    note?: string
  ) => void;
}

interface ResolveFormState {
  note?: string;
  resolvedAt: Date;
}

const AlarmResolveModal: React.FC<AlarmResolveModalProps> = ({
  siteId,
  triggeredDate,
  handleSubmit,
  requestStatus,
  alarmDetails,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const {
    activatedAt,
    alarmId,
    alarmTag: { id: alarmTagId, label, acknowledgementNoteRequired },
    status,
  } = alarmDetails;

  const handleCancel = () => {
    history.push(PathBuilder.ALARM_DETAILS(siteId, alarmId, alarmTagId));
  };

  const filterDates = (dateOption: Date) => {
    return (
      dateOption.getTime() < Date.now() + 1000 * 60 * 5 &&
      dateOption.getTime() > new Date(activatedAt).getTime()
    );
  };

  return (
    <Formik<ResolveFormState>
      validationSchema={getResolveValidationSchema(alarmDetails)}
      onSubmit={(values) => {
        handleSubmit(siteId, alarmTagId, alarmId, values.resolvedAt, values.note);
      }}
      initialValues={{
        note: undefined,
        resolvedAt: new Date(Date.now()),
      }}
      validateOnMount
    >
      {(formikProps) => (
        <MainModal
          title="Resolve Alarm"
          width={512}
          testId="alarm-ack-form"
          secondaryButtonLabel="Cancel"
          disabledSecondaryButton={formikProps.isSubmitting || requestStatus === Lifecycle.LOADING}
          onSecondaryAction={handleCancel}
          onClose={handleCancel}
          isOpen
          height={440}
          primaryButtonLabel="Resolve"
          disabledPrimaryButton={
            formikProps.isValidating || !formikProps.isValid || formikProps.isSubmitting
          }
          onPrimaryAction={formikProps.submitForm}
          isSmall
          headerDivider={false}
        >
          {requestStatus === Lifecycle.LOADING && <LoadWithSpinner />}
          {!requestStatus && (
            <div ref={contentRef}>
              <form onSubmit={formikProps.handleSubmit}>
                <MainInfo status={status} triggeredDate={triggeredDate} label={label} />
                <Field name="resolvedAt">
                  {({ form, field }: FieldProps<Date, ResolveFormState>) => (
                    <DatePicker
                      selected={form.values.resolvedAt}
                      onChange={(e) => field.onChange({ target: { value: e, name: field.name } })}
                      onBlur={field.onBlur}
                      onCalendarClose={() => field.onBlur({ target: { name: field.name } })}
                      dateFormat={DATE_FORMAT}
                      showTimeSelect
                      popperModifiers={{
                        preventOverflow: {
                          enabled: true,
                        },
                      }}
                      filterDate={filterDates}
                      customInput={
                        <TextField
                          {...field}
                          error={Boolean(form.errors.resolvedAt && form.touched.resolvedAt)}
                          helperText={
                            form.errors.resolvedAt &&
                            form.touched.resolvedAt &&
                            form.errors.resolvedAt
                          }
                          fullWidth
                          label="Resolved Date / Time"
                          variant="outlined"
                        />
                      }
                    />
                  )}
                </Field>
                <Field name="note">
                  {({ field }: FieldProps<string | undefined, ResolveFormState>) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      variant="outlined"
                      label={acknowledgementNoteRequired ? 'Note (Required)' : 'Note (Optional)'}
                      margin="normal"
                      className={classes.noteField}
                    />
                  )}
                </Field>
              </form>
            </div>
          )}
        </MainModal>
      )}
    </Formik>
  );
};

export default AlarmResolveModal;
